import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { prefersReducedMotion } from '../../utils/accessibility';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 disabled:opacity-50 disabled:pointer-events-none relative',
  {
    variants: {
      variant: {
        default:
          'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800',
        destructive:
          'bg-error-600 text-white hover:bg-error-700 active:bg-error-800',
        outline:
          'border border-gray-600 bg-transparent hover:bg-gray-700 active:bg-gray-600 text-gray-100 hover:border-gray-500',
        secondary:
          'bg-gray-700 text-gray-100 hover:bg-gray-600 active:bg-gray-500',
        ghost: 'hover:bg-gray-700 active:bg-gray-600 text-gray-100',
        link: 'underline-offset-4 hover:underline text-primary-400 hover:text-primary-300',
      },
      size: {
        default: 'h-10 py-2 px-4',
        sm: 'h-9 px-3 rounded-md text-xs',
        lg: 'h-11 px-8 rounded-md text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button: React.FC<
  ButtonProps & { ref?: React.Ref<HTMLButtonElement> }
> = ({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  loadingText,
  leftIcon,
  rightIcon,
  children,
  disabled,
  ref,
  ...props
}) => {
  const reducedMotion = prefersReducedMotion();
  const isDisabled = disabled || loading;

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <div
            className={cn(
              'w-4 h-4 border-2 border-current border-t-transparent rounded-full',
              !reducedMotion && 'animate-spin',
              (leftIcon || rightIcon) && 'mr-2'
            )}
            aria-hidden="true"
          />
          {loadingText || children}
        </>
      );
    }

    return (
      <>
        {leftIcon && (
          <span className="mr-2" aria-hidden="true">
            {leftIcon}
          </span>
        )}
        {children}
        {rightIcon && (
          <span className="ml-2" aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </>
    );
  };

  return (
    <button
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-busy={loading}
      {...props}
    >
      {renderContent()}
    </button>
  );
};

// Icon button variant for better accessibility
export interface IconButtonProps extends Omit<ButtonProps, 'children'> {
  icon: React.ReactNode;
  'aria-label': string;
  tooltip?: string;
  ref?: React.Ref<HTMLButtonElement>;
}

const IconButton: React.FC<IconButtonProps> = ({
  icon,
  'aria-label': ariaLabel,
  tooltip,
  className,
  ref,
  ...props
}) => {
  return (
    <Button
      ref={ref}
      size="icon"
      className={cn('relative', className)}
      aria-label={ariaLabel}
      title={tooltip || ariaLabel}
      {...props}
    >
      {icon}
    </Button>
  );
};

export { Button, IconButton, buttonVariants };
